import type { ConfigurableServer, Optional } from '@festina-life/core'
import type { QueryKey } from '@tanstack/react-query'

import type { LayerKey } from '../configuration/layers/LayerKey'

const fetchConciseBranchKeyPrefix = ({
  server,
  branchName,
}: {
  server: ConfigurableServer
  branchName: Optional<string>
}): QueryKey => [`/configuration/${server}/branch/${branchName}`]

const fetchConciseWorkspaceKeyPrefix = ({
  server,
  workspaceName,
}: {
  server: ConfigurableServer
  workspaceName: Optional<string>
}): QueryKey => [`/configuration/${server}/workspace/${workspaceName}`]

const fetchConciseBranchKey = ({
  server,
  branchName,
  includeBaseChangesets,
}: {
  server: ConfigurableServer
  branchName: Optional<string>
  includeBaseChangesets: boolean
}): QueryKey => [
  ...fetchConciseBranchKeyPrefix({ server, branchName }),
  includeBaseChangesets ? 'includingBaseChangesets' : 'excludingBaseChangesets',
]

const fetchConciseWorkspaceKey = ({
  server,
  workspaceName,
  includeBaseChangesets,
}: {
  server: ConfigurableServer
  workspaceName: Optional<string>
  includeBaseChangesets: boolean
}): QueryKey => [
  ...fetchConciseWorkspaceKeyPrefix({ server, workspaceName }),
  includeBaseChangesets ? 'includingBaseChangesets' : 'excludingBaseChangesets',
]

const fetchWorkspaceBaseBranchChangesetsKey = ({
  server,
  workspaceName,
}: {
  server: ConfigurableServer
  workspaceName: string
}): QueryKey => [
  `/configuration/shared/server/${server}/workspace/${workspaceName}/changesets`,
]

const allConciseWorkspacesKeyPrefix = 'fetchAllConciseWorkspaces'

const fetchAllConciseWorkspacesKey = (
  forServers: readonly ConfigurableServer[],
): QueryKey => [allConciseWorkspacesKeyPrefix, ...forServers]

const allConciseBranchesKeyPrefix = 'fetchAllConciseBranches'

const fetchAllConciseBranchesKey = (
  forServers: readonly ConfigurableServer[],
): QueryKey => [allConciseBranchesKeyPrefix, ...forServers]

const fetchWorkspaceOperationsKey = ({
  workspaceName,
  server,
}: {
  workspaceName: string
  server: ConfigurableServer
}): QueryKey => [`/fetchWorkspaceOperations/${workspaceName}/${server}`]

const fetchBranchAllErrorsPrefix = ({
  server,
  branchName,
}: {
  server: ConfigurableServer
  branchName: string
}): QueryKey => [`fetchBranchErrorsOnAllLayers`, server, branchName]

const fetchBranchErrorsOnLayersKey = ({
  layers,
  ...rest
}: Parameters<typeof fetchBranchAllErrorsPrefix>[0] & {
  layers: LayerKey[]
}): QueryKey => [
  ...fetchBranchAllErrorsPrefix(rest),
  ...layers.map((layer) => layer.toString()),
]

const fetchAllWorkspacesOnServerErrorsKeyPrefix = ({
  server,
}: {
  server: ConfigurableServer
}) => `/fetchWorkspaceErrors/${server}`

const fetchWorkspaceAllErrorsKeyPrefix = ({
  workspaceName,
  ...others
}: Parameters<typeof fetchAllWorkspacesOnServerErrorsKeyPrefix>[0] & {
  workspaceName: string
}): QueryKey => [
  fetchAllWorkspacesOnServerErrorsKeyPrefix(others),
  workspaceName,
]

const fetchWorkspaceErrorsOnLayersKey = ({
  layers,
  ...rest
}: Parameters<typeof fetchWorkspaceAllErrorsKeyPrefix>[0] & {
  layers: LayerKey[]
}): QueryKey => [
  ...fetchWorkspaceAllErrorsKeyPrefix(rest),
  ...layers.map((layer) => layer.toString()),
]

const fetchLargeOperationsKey = ({
  operationIds,
  componentName,
}: {
  operationIds: string[]
  componentName: ConfigurableServer
}): QueryKey => [
  `/largeOperations/${componentName}`,
  ...operationIds.sort((a, b) => a.localeCompare(b)),
]

export {
  allConciseBranchesKeyPrefix,
  allConciseWorkspacesKeyPrefix,
  fetchAllConciseBranchesKey,
  fetchAllConciseWorkspacesKey,
  fetchAllWorkspacesOnServerErrorsKeyPrefix,
  fetchBranchAllErrorsPrefix,
  fetchBranchErrorsOnLayersKey,
  fetchConciseBranchKey,
  fetchConciseBranchKeyPrefix,
  fetchConciseWorkspaceKey,
  fetchConciseWorkspaceKeyPrefix,
  fetchLargeOperationsKey,
  fetchWorkspaceAllErrorsKeyPrefix,
  fetchWorkspaceBaseBranchChangesetsKey,
  fetchWorkspaceErrorsOnLayersKey,
  fetchWorkspaceOperationsKey,
}
